import { Skeleton, SkeletonProps, Tooltip } from 'antd'
import { TooltipPropsWithTitle } from 'antd/es/tooltip'
import React, { FunctionComponent, ReactNode } from 'react'
import styled from 'styled-components'
import Blur from '@/components/common/blur'
import { Icon } from '@/components/custom-icon'
import { useIntl } from '@/i18n/hooks/use-intl'

export interface KolDetailStatisticProps {
  className?: string
  loading?: boolean | SkeletonProps
  title: string
  value?: 'noData' | 'noDataWithBlur' | ReactNode
  tooltip?: TooltipPropsWithTitle
  prefix?: ReactNode
  suffix?: ReactNode
}

const KolDetailStatistic: FunctionComponent<KolDetailStatisticProps> = ({
  className,
  loading,
  title,
  tooltip,
  prefix,
  value,
  suffix,
}) => {
  const { formatMessage } = useIntl()

  if (loading) {
    const props: SkeletonProps =
      loading === true
        ? {
            active: true,
            title: { width: 150 },
            paragraph: { rows: 1, width: 100 },
          }
        : { ...loading }
    return <StyledSkeleton {...props} />
  }

  return (
    <Box className={className}>
      <TopItems>
        <Title>
          {title}{' '}
          {tooltip && (
            <StyledTooltip {...tooltip}>
              <HelpCircle type='help-circle' />
            </StyledTooltip>
          )}
        </Title>
      </TopItems>
      <BottomItems>
        {prefix}
        {value === 'noData' || value === 'noDataWithBlur' ? (
          <NoData activate={value === 'noDataWithBlur'}>
            {formatMessage({ id: 'autoreport:chart_label_empty_data' })}
          </NoData>
        ) : (
          <>
            <Value>{value}</Value>
            {suffix}
          </>
        )}
      </BottomItems>
    </Box>
  )
}

const StyledSkeleton = styled(Skeleton)`
  width: auto;

  .ant-skeleton-content {
    width: auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .ant-skeleton-title,
  .ant-skeleton-paragraph {
    margin: 0 !important;
  }
`

const TopItems = styled.div`
  display: flex;
  align-items: center;

  > :not(:last-child) {
    margin-right: 3px;
  }
`

const Box = styled.div`
  display: flex;
  flex-direction: column;

  ${TopItems} {
    margin-bottom: 5px;
  }
`

const StyledTooltip = styled(Tooltip)`
  vertical-align: middle;
`

const Title = styled.div`
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`

const HelpCircle = styled(Icon)`
  font-size: 16px;

  svg {
    fill: none;
    stroke: ${({ theme }): string => theme.colors.text.tooltip};
  }
`

export const BottomItems = styled.div`
  display: flex;

  > :not(:last-child) {
    margin-right: 5px;
  }
`

const NoData = styled(Blur)`
  color: ${({ theme }): string => theme.colors.text.placeholder};
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 30.8px */
`

const Value = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 22px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%; /* 30.8px */
`

export default styled(KolDetailStatistic)``
